/**
 * THE ENVIRONMENT STRIP -- top of the sidebar, above the RubyTill mark, on
 * every environment except production (owner ruling 2026-09-27: "so people
 * won't make a mistake about environments ... only prod will be clean").
 *
 *   LOCAL    green   localhost -- and it says which API it talks to, because
 *                    local runs against the real DEV backend
 *   DEV      blue    admin.dev.rubytill.com
 *   STAGING  amber   admin.stage.rubytill.com
 *   PROD     nothing
 *
 * Colours differ in lightness as well as hue, and none is ruby: ruby is the
 * brand, and red reads as "production / danger". The hazard stripe under the
 * band is what keeps it from reading as part of the page.
 *
 * Same component, same rules, in platform-app (components/EnvBanner.tsx).
 */
import React from 'react';

export type AppEnv = 'local' | 'dev' | 'staging' | 'prod';

const viteStage = (): string => {
  try {
    return String((import.meta as any)?.env?.VITE_STAGE || '');
  } catch {
    return '';
  }
};

/** Which environment this page is. Pure over the hostname (+ VITE_STAGE). */
export function detectAppEnv(hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''): AppEnv {
  const h = hostname.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local')) return 'local';
  const v = viteStage();
  if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  if (h.startsWith('admin.dev.') || h.startsWith('dev.') || h.includes('.dev.')) return 'dev';
  if (h.includes('stage.') || h.includes('staging.')) return 'staging';
  return 'prod';
}

/** The backend a LOCAL page talks to (the dev proxy unless VITE_STAGE says otherwise). */
const localApiStage = (): string => {
  const v = viteStage();
  return v === 'prod' || v === 'staging' ? v : 'dev';
};

const STYLE: Record<Exclude<AppEnv, 'prod'>, { label: string; bg: string; fg: string }> = {
  local: { label: 'LOCAL', bg: '#22C55E', fg: '#052E16' },
  dev: { label: 'DEV', bg: '#2563EB', fg: '#FFFFFF' },
  staging: { label: 'STAGING', bg: '#F59E0B', fg: '#1C1400' },
};

export const ENV_DOT: Record<AppEnv, string> = {
  local: '#22C55E', dev: '#2563EB', staging: '#F59E0B', prod: '#22C55E',
};

export const EnvBanner: React.FC<{ env?: AppEnv; collapsed?: boolean }> = ({ env = detectAppEnv(), collapsed }) => {
  if (env === 'prod') return null;
  const s = STYLE[env];
  const host = typeof window !== 'undefined' ? window.location.host : '';
  const right = env === 'local' ? `API → ${localApiStage()}` : host.replace(/\.rubytill\.com$/, '');
  const api = env === 'local' ? localApiStage() : env;
  const full = `${s.label} environment — ${host} · API: ${api}. Not production.`;
  return (
    <div role="status" aria-label={full} title={full} className="flex-shrink-0 select-none" data-testid="env-banner">
      <div
        className="flex items-center gap-2 px-3"
        style={{ background: s.bg, color: s.fg, minHeight: 28, paddingTop: 5, paddingBottom: 5 }}
      >
        <span className="inline-block w-[7px] h-[7px] rounded-full flex-shrink-0" style={{ background: s.fg }} />
        <span className="text-[11px] font-bold tracking-[0.14em]">{collapsed ? s.label.slice(0, 3) : s.label}</span>
        {!collapsed && (
          <span className="ml-auto font-mono text-[10.5px] opacity-90 truncate">{right}</span>
        )}
      </div>
      <div style={{ height: 4, background: `repeating-linear-gradient(135deg, ${s.bg} 0 6px, #000 6px 12px)` }} />
    </div>
  );
};

export default EnvBanner;
